import { useState, useEffect } from 'react'
import { Upload, FileText, Trash2, Download, Image as ImageIcon, Wand2, Eye, Edit2, File, FlaskConical, Link } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import api from '../../../services/api'
import DocumentGeneratorModal from './DocumentGeneratorModal'
import PrescriptionGeneratorModal from './PrescriptionGeneratorModal' // 
import ShareModal from './ShareModal'

export default function PatientDocumentsTab({ patient }) { 
    
    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(true)
    const[uploading, setUploading] = useState(false)
    const [isGeneratorOpen, setIsGeneratorOpen] = useState(false)
    const [isPrescriptionOpen, setIsPrescriptionOpen] = useState(false) // 
    const [shareItem, setShareItem] = useState(null)

    useEffect(() => {
        if(patient?.id) fetchDocuments()
    }, [patient])

    const fetchDocuments = async () => {
        try {
            const res = await api.get(`/api/documents/patient/${patient.id}`)
            setDocuments(res.data)
        } catch (error) { console.error(error) } 
        finally { setLoading(false) }
    }

    const handleFileUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', 'import')

        setUploading(true)
        try {
            await api.post(`/api/documents/patient/${patient.id}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            toast.success("Document ajouté")
            fetchDocuments()
        } catch (error) { toast.error("Erreur lors de l'upload") } 
        finally { setUploading(false); e.target.value = null }
    }

    const handlePreview = async (doc) => {
        try {
            const newWindow = window.open('', '_blank');
            const response = await api.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' })
            let mimeType = 'application/pdf';
            if (['jpg', 'jpeg', 'png', 'webp'].includes(doc.file_type.toLowerCase())) {
                mimeType = `image/${doc.file_type.toLowerCase() === 'jpg' ? 'jpeg' : doc.file_type.toLowerCase()}`;
            }
            const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
            newWindow.location.href = url;
        } catch (error) { toast.error("Erreur lors de l'aperçu") }
    }

    const handleDownload = async (doc) => {
        try {
            const response = await api.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' })
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // On s'assure que l'extension est bien présente pour que l'OS reconnaisse le fichier
            const safeName = doc.name.endsWith(`.${doc.file_type}`) ? doc.name : `${doc.name}.${doc.file_type}`;
            link.setAttribute('download', safeName);
            
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
        } catch (error) { toast.error("Erreur téléchargement") }
    }

    const handleDelete = async (id) => {
        if (!confirm("Supprimer ce document ?")) return
        try {
            await api.delete(`/api/documents/${id}`)
            toast.success("Supprimé")
            fetchDocuments()
        } catch (error) { toast.error("Erreur suppression") }
    }

    const handleRename = async (doc) => {
        const newName = window.prompt("Nouveau nom du document :", doc.name);
        if (!newName || newName.trim() === '' || newName === doc.name) return;
        try {
            await api.patch(`/api/documents/${doc.id}`, { name: newName.trim() });
            toast.success("Document renommé");
            fetchDocuments();
        } catch (error) { toast.error("Erreur lors du renommage"); }
    }

    if (loading) return <div className="p-8 text-center"><div className="spinner"/></div>

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-serif text-sage-700">Documents</h3>
                
                <div className="flex gap-2">
                    {/* NOUVEAU BOUTON PRESCRIPTION */}
                    <button 
                        onClick={() => setIsPrescriptionOpen(true)}
                        className="btn bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-2 py-2 text-sm transition-colors border border-blue-200 shadow-sm"
                    >
                        <FlaskConical className="w-4 h-4" /> Prescription d'Analyses
                    </button>

                    <button 
                        onClick={() => setIsGeneratorOpen(true)}
                        className="btn bg-sage-100 text-sage-700 hover:bg-sage-200 flex items-center gap-2 py-2 text-sm transition-colors"
                    >
                        <Wand2 className="w-4 h-4" /> Créer un courrier
                    </button>

                    <div className="relative">
                        <input 
                            type="file" 
                            onChange={handleFileUpload} 
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={uploading}
                        />
                        <button className="btn btn-primary flex items-center gap-2 py-2 text-sm">
                            {uploading ? <div className="spinner w-4 h-4 text-white"/> : <Upload className="w-4 h-4" />}
                            Importer
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {documents.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 border-2 border-dashed border-gray-50 m-4 rounded-lg">
                        Aucun document
                    </div>
                ) : (
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                            <tr>
                                <th className="px-4 py-3">Nom</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {documents.map((doc) => (
                                <tr key={doc.id} className="hover:bg-sage-50 transition-colors group">
                                <td className="px-4 py-3 flex items-center gap-3 font-medium text-gray-700">
                                    {['jpg', 'png', 'jpeg'].includes(doc.file_type?.toLowerCase()) ? (
                                        <ImageIcon className="w-4 h-4 text-purple-500" />
                                    ) :['doc', 'docx', 'odt'].includes(doc.file_type?.toLowerCase()) ? (
                                        <File className="w-4 h-4 text-blue-600" />
                                    ) : doc.category === 'ordonnance' ? (
                                        <FlaskConical className="w-4 h-4 text-blue-600" />
                                    ) : (
                                        <FileText className="w-4 h-4 text-red-500" />
                                    )}
                                    {doc.name}
                                    <button onClick={() => handleRename(doc)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-sage-600 ml-2" title="Renommer">
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-xs uppercase text-gray-400">{doc.category}</td>
                                <td className="px-4 py-3 text-gray-500">
                                    {format(new Date(doc.created_at), 'dd/MM/yyyy')}
                                </td>
                                <td className="px-4 py-3 text-right flex justify-end gap-2">
                                    {/* On n'affiche le bouton Aperçu que si c'est une image ou un PDF */}
                                    {['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(doc.file_type?.toLowerCase()) && (
                                        <button onClick={() => handlePreview(doc)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded border border-transparent hover:border-blue-200 transition-all" title="Aperçu">
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button onClick={() => handleDownload(doc)} className="p-1.5 text-gray-400 hover:text-sage-600 hover:bg-white rounded border border-transparent hover:border-sage-200 transition-all" title="Télécharger">
                                        <Download className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setShareItem({ type: 'document', id: doc.id, title: doc.name })} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded border border-transparent hover:border-blue-200 transition-all" title="Partager au réseau">
                                        <Link className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded border border-transparent hover:border-red-200 transition-all"><Trash2 className="w-4 h-4" /></button>
                                </td>
                                </tr>
                            ))}
                            </tbody>
                    </table>
                )}
            </div>

            {isGeneratorOpen && <DocumentGeneratorModal patient={patient} onClose={() => setIsGeneratorOpen(false)} onSuccess={fetchDocuments} />}
            {isPrescriptionOpen && <PrescriptionGeneratorModal patient={patient} onClose={() => setIsPrescriptionOpen(false)} onSuccess={fetchDocuments} />}
            {shareItem && (
                <ShareModal 
                    itemType={shareItem.type} 
                    itemId={shareItem.id} 
                    itemTitle={shareItem.title} 
                    patient={patient} 
                    onClose={() => setShareItem(null)} 
                />
            )}
        </div>
    )
}